import React from "react";
import {
  Container,
  Grid,
  Typography,
  Paper,
  Box,
  useTheme,
  alpha,
  useMediaQuery,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  Receipt as InvoiceIcon,
  Dashboard as DashboardIcon,
  CheckCircle as CheckIcon,
  AutoAwesome as PremiumIcon,
  Download as DownloadIcon,
  Email as EmailIcon,
  Share as ShareIcon,
  Security as SecurityIcon,
  PictureAsPdf as PdfIcon,
  Analytics as AnalyticsIcon,
  Schedule as ScheduleIcon,
  Groups as GroupsIcon,
  Payment as PaymentIcon,
  CloudSync as SyncIcon,
  Smartphone as MobileIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";

// Import your images
import invoiceDashboard from "./Images/invoiceBanner.png";
import invoiceCreate from "./Images/invoiceFeture.png";
import invoiceTemplates from "./Images/invoiceFormat.png";
import invoiceMobile from "./Images/accounting.png";

const Invoicing = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const PremiumCard = styled(Card)(({ theme }) => ({
    background: `linear-gradient(135deg, ${alpha(
      theme.palette.primary.main,
      0.05
    )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    borderRadius: 20,
    padding: theme.spacing(3),
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
    },
  }));

  const features = [
    {
      title: "Professional Templates",
      description: "20+ customizable templates with your branding",
      icon: <PremiumIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.primary.main,
    },
    {
      title: "Auto-GST Calculation",
      description: "Smart GST computation with HSN/SAC codes",
      icon: <PaymentIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.success.main,
    },
    {
      title: "Instant Sharing",
      description: "Email, WhatsApp, or direct link sharing",
      icon: <ShareIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.info.main,
    },
    {
      title: "Payment Tracking",
      description: "Real-time payment status and reminders",
      icon: <AnalyticsIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.warning.main,
    },
    {
      title: "Multi-Format Export",
      description: "PDF, Excel, Word with professional formatting",
      icon: <DownloadIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.error.main,
    },
    {
      title: "Recurring Invoices",
      description: "Automate repeat billing with schedules",
      icon: <ScheduleIcon sx={{ fontSize: 32 }} />,
      color: theme.palette.secondary.main,
    },
  ];

  const templates = [
    {
      name: "Classic Professional",
      description: "Clean, corporate design for B2B invoices",
      color: "#1976d2",
    },
    {
      name: "Modern Minimal",
      description: "Sleek design for creative businesses",
      color: "#2e7d32",
    },
    {
      name: "Retail Elegant",
      description: "Perfect for retail and e-commerce",
      color: "#ed6c02",
    },
    {
      name: "Service Premium",
      description: "Professional service industry template",
      color: "#9c27b0",
    },
  ];

  return (
    <Box
      sx={{
        pb: 8,
        background: "linear-gradient(135deg, #f8faff 0%, #ffffff 100%)",
      }}
    >
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.08
          )} 0%, ${alpha(theme.palette.secondary.main, 0.08)} 100%)`,
          py: { xs: 8, md: 12 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={6}
            alignItems="center"
            direction={{ xs: "column-reverse", md: "row" }}
          >
            {/* Left Content */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Chip
                icon={<PremiumIcon />}
                label="Premium Invoicing Solution"
                color="primary"
                variant="filled"
                sx={{
                  mb: 3,
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                }}
              />

              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  mb: 3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  fontSize: { xs: "2.5rem", md: "3rem" },
                  lineHeight: 1.1,
                }}
              >
                Professional Invoices in Seconds
              </Typography>

              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ mb: 4, lineHeight: 1.6, fontWeight: 400 }}
              >
                Create, send, and track beautiful invoices that get you paid
                faster. GST-compliant, multi-currency, and fully automated for
                your business.
              </Typography>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mb: 4 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 700,
                    borderRadius: 3,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    boxShadow: "0 8px 25px rgba(25, 118, 210, 0.3)",
                    "&:hover": {
                      boxShadow: "0 12px 35px rgba(25, 118, 210, 0.4)",
                    },
                  }}
                  startIcon={<InvoiceIcon />}
                >
                  Create Your First Invoice
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontWeight: 600,
                    borderRadius: 3,
                    borderWidth: 2,
                    "&:hover": {
                      borderWidth: 2,
                    },
                  }}
                >
                  Watch Demo
                </Button>
              </Stack>

              {/* Trust Indicators */}
              <Stack direction="row" spacing={4} sx={{ mt: 4 }}>
                <Box>
                  <Typography variant="h4" fontWeight={800} color="primary">
                    50K+
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Invoices Created
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={800} color="primary">
                    98%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Faster Payments
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={800} color="primary">
                    4.9★
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Customer Rating
                  </Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Right Content - Hero Image */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  position: "relative",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: -20,
                    right: -20,
                    bottom: -20,
                    left: -20,
                    background: `linear-gradient(135deg, ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )} 0%, transparent 50%)`,
                    borderRadius: 30,
                    zIndex: 0,
                  },
                }}
              >
                <Box
                  component="img"
                  src={invoiceDashboard}
                  alt="Professional Invoicing Dashboard"
                  sx={{
                    width: "100%",
                    height: "auto",
                    borderRadius: 4,
                    boxShadow: "0 25px 50px rgba(0, 0, 0, 0.15)",
                    backgroundColor: "transparent",
                    position: "relative",
                    zIndex: 1,
                    transform:
                      "perspective(1000px) rotateY(-5deg) rotateX(5deg)",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform:
                        "perspective(1000px) rotateY(0deg) rotateX(0deg)",
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Grid */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" sx={{ mb: 6 }}>
          <Typography variant="h3" fontWeight={700} sx={{ mb: 2 }}>
            Everything You Need for
            <Box component="span" color="primary.main">
              {" "}
              Perfect Invoices
            </Box>
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 700, mx: "auto" }}
          >
            From creation to payment, our invoicing system handles every detail
            with precision and style.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid size={{ xs: 12, md: 4 }} key={index}>
              <PremiumCard>
                <CardContent sx={{ textAlign: "center", p: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: alpha(feature.color, 0.1),
                      color: feature.color,
                      width: 70,
                      height: 70,
                      mb: 3,
                      mx: "auto",
                      border: `2px solid ${alpha(feature.color, 0.2)}`,
                    }}
                  >
                    {feature.icon}
                  </Avatar>
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </PremiumCard>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Template Showcase */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid
          container
          spacing={6}
          alignItems="center"
          justifyContent={"center"}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h3" fontWeight={700} sx={{ mb: 3 }}>
              <span style={{ color: "#1976d2" }}>Stunning Templates</span> That
              Reflect Your Brand
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Choose from professionally designed templates that make your
              invoices stand out. Customize colors, fonts, and layout to match
              your brand identity.
            </Typography>

            <Grid container spacing={2}>
              {templates.map((template, index) => (
                <Grid size={{ xs: 12, md: 6 }} key={index}>
                  <Card
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      border: `2px solid ${alpha(template.color, 0.1)}`,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        borderColor: alpha(template.color, 0.3),
                        transform: "translateY(-4px)",
                        boxShadow: `0 8px 25px ${alpha(template.color, 0.15)}`,
                      },
                      height: "70%",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          bgcolor: template.color,
                          mr: 1.5,
                        }}
                      />
                      <Typography variant="h6" fontWeight={600}>
                        {template.name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {template.description}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src={invoiceTemplates}
              alt="Invoice Templates"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Advanced Features */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid
          container
          spacing={6}
          alignItems="center"
          direction={{ xs: "column-reverse", md: "row" }}
        >
          <Grid size={{ xs: 12, md: 6 }}>
            <Box
              component="img"
              src={invoiceCreate}
              alt="Create Invoice"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="h3" fontWeight={700} sx={{ mb: 3 }}>
              <span style={{ color: "#1976d2" }}>Smart Invoicing</span> with
              Advanced Features
            </Typography>

            <List sx={{ width: "100%" }}>
              {[
                "Auto-calculate taxes (GST, VAT, Sales Tax)",
                "Multi-currency support for international clients",
                "Recurring invoice automation",
                "Late payment reminders and fees",
                "Discounts and promotional codes",
                "Inventory and stock level integration",
                "Client portal for payment history",
                "Real-time collaboration with your team",
              ].map((feature, index) => (
                <ListItem key={index} sx={{ px: 0, py: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <CheckIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary={feature}
                    primaryTypographyProps={{ variant: "h6", fontWeight: 500 }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Container>

      {/* Mobile Experience */}
      {/* <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" fontWeight={800} sx={{ mb: 3 }}>
              Invoicing On The Go
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Create and send invoices from anywhere with our mobile-optimized
              experience. Perfect for field sales, service businesses, and
              entrepreneurs on the move.
            </Typography>

            <Stack spacing={3}>
              {[
                "Create invoices in under 60 seconds",
                "Capture receipts with your camera",
                "Offline capability with auto-sync",
                "Push notifications for payments",
                "Digital signatures on-site",
                "GPS-based client location",
              ].map((feature, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center" }}>
                  <MobileIcon color="primary" sx={{ mr: 2 }} />
                  <Typography variant="h6" fontWeight={500}>
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src={invoiceMobile}
              alt="Mobile Invoicing"
              sx={{
                width: "100%",
                height: "auto",
                borderRadius: 4,
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
              }}
            />
          </Grid>
        </Grid>
      </Container> */}

      {/* Integration & Compliance */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <PremiumCard>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
                <span style={{ color: "#1976d2" }}>Seamless Integration</span>{" "}
                with RiteAccount
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                Your invoices automatically sync with accounting ledgers, GST
                reports, and financial statements in real-time.
              </Typography>

              <Stack spacing={2}>
                {[
                  "Auto-post to Sales and Purchase registers",
                  "Instant GST calculation and reporting",
                  "Real-time profit and loss updates",
                  "Automated bank reconciliation",
                  "Multi-branch and multi-warehouse support",
                  "Audit-ready with complete transaction trail",
                ].map((item, index) => (
                  <Box
                    key={index}
                    sx={{ display: "flex", alignItems: "flex-start" }}
                  >
                    <SyncIcon color="success" sx={{ mr: 2, mt: 0.5 }} />
                    <Typography variant="body1" fontWeight={500}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
                <span style={{ color: "#1976d2" }}>GST & Tax</span> Compliance
                Made Easy
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                Full compliance with Indian tax regulations including GST, TDS,
                and international tax requirements.
              </Typography>

              <Grid container spacing={2}>
                {[
                  "GSTR-1 & GSTR-3B ready exports",
                  "E-way bill integration",
                  "HSN/SAC code automation",
                  "Tax credit tracking",
                  "Multi-state GST compliance",
                  "International tax support",
                ].map((feature, index) => (
                  <Grid size={{ xs: 12 }} key={index}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <SecurityIcon
                        color="primary"
                        sx={{ mr: 1, fontSize: 20 }}
                      />
                      <Typography variant="body1" fontWeight={500}>
                        {feature}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </PremiumCard>
      </Container>

      {/* Final CTA */}
      {/* <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
        <PremiumCard>
          <Typography variant="h3" fontWeight={800} sx={{ mb: 2 }}>
            Ready to Transform Your Invoicing?
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
            Join thousands of businesses using our premium invoicing solution.
            Start free today – no credit card required.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 700,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                fontSize: "1.1rem",
              }}
              startIcon={<InvoiceIcon />}
            >
              Start Creating Invoices
            </Button>
            <Button
              variant="outlined"
              size="large"
              sx={{
                px: 5,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 3,
                borderWidth: 2,
                fontSize: "1.1rem",
              }}
            >
              Schedule Personalized Demo
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            ✓ 14-day free trial • ✓ No setup fees • ✓ Cancel anytime
          </Typography>
        </PremiumCard>
      </Container> */}
    </Box>
  );
};

export default Invoicing;
